import { Injectable } from '@angular/core';
import { User } from '@equipmyschool/emsadminweb-models';
import { AccountService } from 'app/server/server.module';
import {
  ClientDetails,
   PtdDispatchDetails,
  PreDispatchDetails,
  Proforma,
} from 'coadapi';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class sharedUserData {
  user: User;

  constructor(private server: AccountService) {
    // Eagerly cache the current user once so callers can synchronously access ContactID
    this.server.iAmBehaviourSubject.subscribe((next) => {
      this.user = next;
    });
  }

  getUser(): User {
    return this.user;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SharedDispatchData {
  clientShare: ClientDetails = {};
  dispatchShare:  PtdDispatchDetails = {};
  preDispatchShare: PreDispatchDetails = {};
  sameAsConsigneeFlag: boolean = false;
  lastRoutePosition: string[]= [];
  links: { label: string; link: string; index: number }[] = [];
  iwdShare: PtdDispatchDetails = {};
  originShare: PtdDispatchDetails = {};
  /// Utils.mapDetailsToUpdate is used to populate the predispatchshare object with current details when the dispatch is being edited

  setData(tempClient, tempDispatch): void {
    this.clientShare = tempClient;
    this.dispatchShare = tempDispatch;
    return;
  }

  setIWDData(tempClient, tempOriginDispatch, tempIWD): void {
    this.clientShare = tempClient;
    this.originShare = tempOriginDispatch;
    this.dispatchShare = tempIWD;
    return;
  }

  reset(): void {
    this.clientShare = {};
    this.dispatchShare = {};
    this.preDispatchShare = {};
    this.sameAsConsigneeFlag = false;
    this.lastRoutePosition = [];
  }

  getClientSharedData(): ClientDetails {
    return this.clientShare;
  }

  getDispatchSharedData():  PtdDispatchDetails {
    return this.dispatchShare;
  }

  getOriginSharedData(): PtdDispatchDetails{
    return this.originShare;
  }

  getPreDispatchSharedData(): PreDispatchDetails {
    return this.preDispatchShare;
  }

  setPreDispatch(preDispatch: PreDispatchDetails) {
    this.preDispatchShare = preDispatch;
  }

  setPreDispatchClient(id: number): void {
    this.preDispatchShare.varClient = id;
  }

  setPreDispatchMethod(id: number): void {
    this.preDispatchShare.method = id;
  }

  setPreDispatchIncoterm(id?: number): void {
    this.preDispatchShare.incoterm = id;
  }

  setPreDispatchIncotermLocation(string?: string): void {
    this.preDispatchShare.incotermLocation = string;
  }

  setPreDispatchEts(dateString: string): void {
    this.preDispatchShare.ets = dateString;
  }

  setPreDispatchEta(dateString: string): void {
    this.preDispatchShare.eta = dateString;
  }

  setPreDispatchExternalEts(dateString?: string | null): void {
    this.preDispatchShare.externalEts = dateString ?? null;
  }

  setPreDispatchDeliveryAddress(id: number): void {
    this.preDispatchShare.deliveryAddress = id;
  }

  getPreDispatchDeliveryAddress(): number | null {
    return this.preDispatchShare.deliveryAddress ?? null;
  }

  setPreDispatchConsigneeAddress(id: number): void {
    this.preDispatchShare.consigneeAddress = id;
  }

  setPreDispatchWarehouse(id: number): void {
    this.preDispatchShare.warehouse = id;
  }

  setPreDispatchSplitShipment(boolean: boolean): void {
    if (boolean) {
      this.preDispatchShare.shipmentCategory = 1;
    } else {
      this.preDispatchShare.shipmentCategory = 2;
    }
  }

  setPreDispatchShipmentCategory(id:number): void {
    this.preDispatchShare.shipmentCategory = id;
  }


  setPreDispatchDescriptive(id: number): void {
    this.preDispatchShare.descriptive = id;
  }

  setSameAsConsigneeFlag(boolean:boolean): void {
      this.sameAsConsigneeFlag = boolean;
  }

  getSameAsConsigneeFlag(): boolean {
    return this.sameAsConsigneeFlag;
  }

  setLastRoutePosition(string:string): void {
    if (this.lastRoutePosition[this.lastRoutePosition.length-1] !== string){
      this.lastRoutePosition.push(string);
    };          
  }

  getLastRoutePosition(): string {
    this.lastRoutePosition.pop();
    const backToHereString: string = this.lastRoutePosition[this.lastRoutePosition.length-1]
    return backToHereString;
  }

  setLinks(links: { label: string; link: string; index: number }[]): void {
    this.links = links;
  }

  getLinks(): { label: string; link: string; index: number }[] {
    return this.links;
  }

  getNextLink(label: string, skip: boolean): string {
    const normalizedLabel = label.toLowerCase().replace(/^\.\//, '');
    const currentIndex = this.links.findIndex(
      (link) => link.link.toLowerCase().replace(/^\.\//, '') === normalizedLabel
    );

    // Fallback safely to the first step when label lookup fails.
    const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
    const rawNextIndex = safeCurrentIndex + (skip ? 2 : 1);
    const nextIndex = Math.min(rawNextIndex, this.links.length - 1);
    const nextLink = this.links[nextIndex]?.link?.replace(/^\.\//, '') ?? 'finalize';

    return "../" + nextLink;
  }

  getLinkIndex(label: string): number {
    return this.links.find((link) => link.link.includes(label))?.index ?? -1;
  }

  setPreDispatchClientAgent(id: number): void {
    this.preDispatchShare.clientAgent = id;
  }

  getPreDispatchClientAgent(): number {
  return this.preDispatchShare.clientAgent;
  }

  setPreDispatchClientContact(id: number): void {
    this.preDispatchShare.clientContact = id;
  }

  getPreDispatchClientContact(): number {
  return this.preDispatchShare.clientContact;
  }

  setPreDispatchClientAgentContact(id: number): void {
    this.preDispatchShare.clientAgentContact = id;
  }

  getPreDispatchClientAgentContact(): number {
  return this.preDispatchShare.clientAgentContact;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SharedProformaData {
  proformaShare: Proforma = {};

  setData(tempProforma): void {
    this.proformaShare = tempProforma;
    return;
  }

  reset(): void {
    this.proformaShare = {};
  }

  getProformaSharedData(): Proforma {
    return this.proformaShare;
  }
}


@Injectable({
  providedIn: 'root'
})
export class StateService {
  drawerState$ = new BehaviorSubject<Map<number, boolean>>(null);
  flipState$ = new BehaviorSubject<Map<number, boolean>>(null);
}

@Injectable({
  providedIn: 'root'
})
export class ClientActionService {
  clientAction$ = new Subject<void>();
}

@Injectable({
  providedIn: 'root'
})
export class DialogCompleteService {
  dialogComplete$ = new Subject<boolean>();
}

@Injectable({
  providedIn: 'root'
})
export class DDActionService {
  ddAction$ = new Subject<void>();
}

